/* eslint-disable react-hooks/exhaustive-deps */

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { LATEST_VIEW_DASHBOARD_LIBRARY } from '../../../helpers/constants';
import {
    getOrderedFavoriteDashboards,
    getRunningDashboardId,
    loadAccordionsState,
    saveAccordionsState,
    setLatestViewInDashboards
} from '../../../helpers/dashboard-helpers';
import isEqual from 'lodash/isEqual';
import { INITIAL_DASHBOARD_FILTER_PARAMS } from '../../../store/user/constants';
import Loader from '../../../components/common/loader/Loader';
import DashboardLayoutSwitch from '../../../components/dashboard/dashboard-layout-switch/dashboard-layout-switch';
// eslint-disable-next-line max-len
import DashboardLibraryAccordion from '../../../components/dashboard/dashboard-library-accordion/dashboard-library-accordion';
import EmptyContent from '../../../components/common/empty-content/empty-content';
import Modal from '../../../components/common/modal/modal';
import DashboardSettingsModal from '../../../components/dashboard/settings-modal/settings-modal';
import RightSideBar from '../../../components/common/right-side-bar/right-side-bar';
import DashboardAndWidgetInfo from '../../../components/dashboard/dashboard-and-widget-info/dashboard-and-widget-info';
import ShortcutOpen from '../../../components/dashboard/hotkeys/shortcut-open';
import OnboardingTooltipContainer from '../../../components/onboarding/OnboardingTooltipContainer';
import Onboarding from '../../../components/onboarding/onboarding';
import DashboardLibraryHeaderMain
    from '../../../components/dashboard/dashboard-library-header-main/DashboardLibraryHeaderMain';
import {
    changeDashboardLibraryFilters,
    changeDashboardLibraryLayout,
    resetDashboardLibraryFilters,
    setDashboardLibraryFilters
} from '../../../store/user/user-actions';
import { steveDataStorage } from '../../../helpers/sdk-helpers';
import {
    isMobile
} from 'react-device-detect';
import {
    deleteDashboard,
    removeAllDashboardsForExport,
    setDashboardExportMode,
    updateModalFormData,
    importDashboards,
    exportDashboards,
    getAllDashboards
} from '../../../store/dashboard/dashboard-actions';
/* styles */
import './dashboard-library.scss';
import {
    DASHBOARD_FILTER_SECTION,
    DASHBOARD_ITEM_SECTION,
    DASHBOARD_LIBRARY_PAGE_LABEL,
    DASHBOARD_LIST_ITEM_SECTION
} from '../../../components/onboarding/constants';
import DashboardLibraryHeaderExport
    from '../../../components/dashboard/dashboard-library-header-export/DashboardLibraryHeaderExport';

const DashboardLibrary = ({
    showDashboardsFilter,
    resourcesLoaded,
    favoriteDashboards,
    otherDashboards,
    dashboards,
    history,
    updateModalFormData,
    deleteDashboard,
    queryParams,
    defaultImageBase64,
    isFetchingDashboards,
    isMobileDevice,
    permissionSettingOptions,
    imageUploadOptions,
    shortcutAndHotkeysOptions,
    selectedOrganization,
    isHelpIntroDisabled,
    setDashboardExportMode,
    isExportMode,
    dashboardsForExport,
    importDashboards,
    exportDashboards,
    removeAllDashboardsForExport,
    dashboardFiltersLoaded,
    changeDashboardLibraryLayout,
    setDashboardLibraryFilters,
    getAllDashboards,
    dashboardLayout,
    resetDashboardLibraryFilters
}) => {
    const [deleteDashboardItem, setDeleteDashboardItem] = useState(null);
    const [isSettingsModalShown, setIsSettingsModalShown] = useState(false);
    const [areFiltersShown, setAreFiltersShown] = useState(showDashboardsFilter);
    const [dashboardInfo, setDashboardInfo] = useState(null);
    const [runningDashboardId, setRunningDashboardId] = useState(null);
    const [favoritesAccordionOpened, setFavoritesAccordionOpened] = useState(true);
    const [otherAccordionOpened, setOtherAccordionOpened] = useState(true);
    const [isMobileMenuShown, setIsMobileMenuShown] = useState(false);
    const [isImportDashboardsWindowVisible, setIsImportDashboardsWindowVisible] = useState(false);
    const noDashboards = useMemo(
        () => !otherDashboards.length && !favoriteDashboards.length,
        [otherDashboards, favoriteDashboards]
    );
    const pageClassName = useMemo(
        () => `glu-page__body${isMobileMenuShown ? ' glu-page__body--margin-top' : ''}`,
        [isMobileMenuShown]
    );
    const orderedFavoriteDashboards = useMemo(
        () => getOrderedFavoriteDashboards(favoriteDashboards),
        [favoriteDashboards]
    );
    const componentIsMounted = useRef();

    const unsetDashboardInfo = () => setDashboardInfo(null);

    const fetchAccordionsState = () => {
        const accordionsState = loadAccordionsState();
        if (!accordionsState) {
            return;
        }

        setFavoritesAccordionOpened(accordionsState.favorites);
        setOtherAccordionOpened(accordionsState.other);
    };

    const toggleFavoritesAccordionOpened = () => {
        saveAccordionsState({
            favorites: !favoritesAccordionOpened,
            other: otherAccordionOpened
        });
        setFavoritesAccordionOpened(!favoritesAccordionOpened);
    };

    const toggleOtherAccordionOpened = () => {
        saveAccordionsState({
            favorites: favoritesAccordionOpened,
            other: !otherAccordionOpened
        });
        setOtherAccordionOpened(!otherAccordionOpened);
    };

    const setRunningDashboardToLocalState = useCallback(async () => {
        let runningDashboardId = await getRunningDashboardId(favoriteDashboards);
        if (!runningDashboardId || !dashboards.find(db => db.id === runningDashboardId)) {
            runningDashboardId = null;
        }
        if (componentIsMounted.current) {
            setRunningDashboardId(runningDashboardId);
        }
    }, [dashboards, favoriteDashboards]);

    const toggleSettingsModal = () => setIsSettingsModalShown(!isSettingsModalShown);

    const toggleFilters = () => {
        setAreFiltersShown(value => !value);
    };

    const openDashboardPage = dashboard => () => history.push(`/dashboard/view/${dashboard.id}`);

    const handleModalDataSave = data => {
        updateModalFormData(data);
        history.push('/dashboard/new');
    };

    const toggleDashboardModal = dashboard => () => setDeleteDashboardItem(dashboard);

    const handleDashboardDelete = () => {
        if (deleteDashboardItem) {
            deleteDashboard(
                deleteDashboardItem,
                !isEqual(queryParams, INITIAL_DASHBOARD_FILTER_PARAMS) ? queryParams : null
            );
            setDeleteDashboardItem(null);
            setDashboardInfo(null);
        }
    };

    const handleDashboardInfoClick = dashboard => () => setDashboardInfo(dashboard);

    const handleToggleMobileMenu = () => setIsMobileMenuShown(!isMobileMenuShown);

    const onDashboardInfoClose = () => setDashboardInfo(null);

    const getImage = modalFormData => {
        if (modalFormData.selectedImageOptionId === 'DEFAULT') {
            return modalFormData.defaultImage || defaultImageBase64;
        }
        if (modalFormData.selectedImageOptionId === 'CUSTOM') {
            return modalFormData.image;
        }
        return null;
    };

    const goToRunningDashboard = id => () => history.push(`/dashboard/view/${id}`);

    const activateDashboardExportMode = () => {
        setDashboardExportMode(true);
    };

    const showImportDashboardsWindow = () => {
        setIsImportDashboardsWindowVisible(true);
    };

    const onImportDashboardsWindowClose = () => {
        setIsImportDashboardsWindowVisible(false);
    }

    const cancelDashboardExportMode = () => {
        removeAllDashboardsForExport();
        setDashboardExportMode(false);
    };

    const onExportDashboards = () => {
        exportDashboards(dashboardsForExport);
        cancelDashboardExportMode();
    }

    const fetchDashboardLayout = () => {
        const layout = isMobile ? 'grid' : steveDataStorage('dashboards', 'view', false).get('layout') || 'grid';
        changeDashboardLibraryLayout(layout);
    };

    const getDashboardLibraryFilters = () => {
        const filtersData = {}
        const queryParams = steveDataStorage('dashboards_library', 'filters', true).get('query_params');
        if (!queryParams) {
            resetDashboardLibraryFilters();
        }
        if (queryParams) {
            filtersData.showDashboardsFilter = !isEqual(queryParams, INITIAL_DASHBOARD_FILTER_PARAMS);
            filtersData.queryParams = queryParams;
            setAreFiltersShown(filtersData.showDashboardsFilter)
            if (isMobile) {
                filtersData.showDashboardsFilter = false;
            }
            setDashboardLibraryFilters(filtersData);
        }
    };

    const getLibraryInitData = () => {
        fetchDashboardLayout();
        getDashboardLibraryFilters();
    }

    useEffect(() => {
        getLibraryInitData();
    }, [selectedOrganization])

    useEffect(() => {
        if (!dashboardFiltersLoaded) {
            return;
        }

        if (dashboards.length) {
            return;
        }

        getAllDashboards(queryParams);
    }, [queryParams, dashboardFiltersLoaded]);

    useEffect(() => {
        componentIsMounted.current = true;
        fetchAccordionsState();
        setLatestViewInDashboards(LATEST_VIEW_DASHBOARD_LIBRARY);
        return () => {
            componentIsMounted.current = false;
        };
    }, []);

    useEffect(() => {
        if (resourcesLoaded.dashboards) {
            setRunningDashboardToLocalState();
        }
    }, [favoriteDashboards, resourcesLoaded.dashboards, setRunningDashboardToLocalState]);

    useEffect(() => {
        unsetDashboardInfo();
    }, [selectedOrganization]);

    return (
        <div className="glu-content">
            {isFetchingDashboards && <Loader />}
            {
                isHelpIntroDisabled
                    ? (
                        <>
                            <OnboardingTooltipContainer
                                key={DASHBOARD_FILTER_SECTION}
                                page={DASHBOARD_LIBRARY_PAGE_LABEL}
                                section={DASHBOARD_FILTER_SECTION}
                            />
                            <OnboardingTooltipContainer
                                key={dashboardLayout === 'list'
                                    ? DASHBOARD_LIST_ITEM_SECTION
                                    : DASHBOARD_ITEM_SECTION
                                }
                                page={DASHBOARD_LIBRARY_PAGE_LABEL}
                                section={dashboardLayout === 'list'
                                    ? DASHBOARD_LIST_ITEM_SECTION
                                    : DASHBOARD_ITEM_SECTION
                                }
                            />
                        </>

                    )
                    : null
            }
            <div
                className={`glu-page ${areFiltersShown
                    ? 'glu-page--has-filters-opened'
                    : 'glu-page--has-header'
                    } `}
            >{!isExportMode
                ? (
                    <DashboardLibraryHeaderMain
                        isMobileMenuShown={isMobileMenuShown}
                        isMobileDevice={isMobileDevice}
                        runningDashboardId={runningDashboardId}
                        goToRunningDashboard={goToRunningDashboard}
                        toggleSettingsModal={toggleSettingsModal}
                        activateDashboardExportMode={activateDashboardExportMode}
                        showImportDashboardsWindow={showImportDashboardsWindow}
                        toggleFilters={toggleFilters}
                        areFiltersShown={areFiltersShown}
                        isImportDashboardsWindowVisible={isImportDashboardsWindowVisible}
                        handleToggleMobileMenu={handleToggleMobileMenu}
                        onImportDashboardsWindowClose={onImportDashboardsWindowClose}
                        onImportDashboards={importDashboards}
                        isFetchingDashboards={isFetchingDashboards}
                    />
                ) : (
                    <DashboardLibraryHeaderExport
                        isMobileMenuShown={isMobileMenuShown}
                        cancelDashboardExportMode={cancelDashboardExportMode}
                        onExportDashboards={onExportDashboards}
                        numberOfDashboardsForExport={dashboardsForExport?.length || 0}
                    />
                )}
                <div className={pageClassName}>
                    {!isMobileDevice && <DashboardLayoutSwitch />}
                    {
                        noDashboards
                            && isEqual(queryParams, INITIAL_DASHBOARD_FILTER_PARAMS)
                            ? (
                                <div className="glu-dashboard-library__info glu-flex glu-flex-col text-center">
                                    <div>
                                        <div className="glu-h2">
                                            Your Dashboard library is empty.
                                        </div>
                                        <div className="glu-h2">
                                            Start by creating a new dashboard.
                                        </div>
                                    </div>
                                    <div
                                        className="glu-dashboard-library__info-btn glu-flex"
                                        onClick={toggleSettingsModal}
                                    >
                                        <div className="glu-h3">
                                            <span className="g-icon g-icon-dashboard-add" />
                                            <div>Create new dashboard</div>
                                        </div>
                                    </div>
                                    <div className="glu-dashboard-library__share-disclaimer glu-hide-md">
                                        <span className="g-icon g-icon-info" />
                                        <p className="glu-dashboard-library__share-disclaimer-text">
                                            Tip: You can easily share your dashboard
                                            with others.
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    {!noDashboards ? (
                                        <div>
                                            <div className="glu-dashboard-library">
                                                <DashboardLibraryAccordion
                                                    openDashboardPage={openDashboardPage}
                                                    handleDashboardInfoClick={handleDashboardInfoClick}
                                                    toggleDashboardModal={toggleDashboardModal}
                                                    getImage={getImage}
                                                    items={orderedFavoriteDashboards}
                                                    title="Favorite dashboards"
                                                    infoTitle="other"
                                                    history={history}
                                                    reorderingAllowed={!isMobileDevice}
                                                    accordionOpened={favoritesAccordionOpened}
                                                    toggleAccordion={toggleFavoritesAccordionOpened}
                                                />
                                            </div>
                                            <div className="glu-dashboard-library">
                                                <DashboardLibraryAccordion
                                                    openDashboardPage={openDashboardPage}
                                                    handleDashboardInfoClick={handleDashboardInfoClick}
                                                    toggleDashboardModal={toggleDashboardModal}
                                                    getImage={getImage}
                                                    items={otherDashboards}
                                                    title="Other dashboards"
                                                    infoTitle="favorite"
                                                    history={history}
                                                    accordionOpened={otherAccordionOpened}
                                                    toggleAccordion={toggleOtherAccordionOpened}
                                                />
                                            </div>
                                        </div>
                                    ) : (
                                        <EmptyContent
                                            title="No search results"
                                            info="Try again with different search criteria."
                                        />
                                    )}
                                </>
                            )
                    }
                </div>
            </div>
            {deleteDashboardItem && (
                <Modal.Delete
                    itemLabel={deleteDashboardItem.title}
                    isOpen={!!deleteDashboardItem}
                    onDelete={handleDashboardDelete}
                    onCancel={toggleDashboardModal(null)}
                >
                    <div className="glu-h5">
                        {deleteDashboardItem.modalFormData
                            .selectedPermissionSettingsId === 2 && (
                                <>
                                    This dashboard is shared with users in
                                    your organization. <br />
                                    Deleting it will cause them to lose access.{' '}
                                    <br />
                                </>
                            )}
                        Are you sure you want to delete this dashboard? <br />
                    </div>
                </Modal.Delete>
            )}
            {isSettingsModalShown && (
                <DashboardSettingsModal
                    onClose={toggleSettingsModal}
                    onSave={handleModalDataSave}
                    defaultImage={defaultImageBase64}
                    permissionSettingsOptions={permissionSettingOptions}
                    imageUploadOptions={imageUploadOptions}
                />
            )}
            <RightSideBar isCollapsed={!dashboardInfo}>
                {dashboardInfo ? (
                    <DashboardAndWidgetInfo
                        description={dashboardInfo.description}
                        createdBy={dashboardInfo.createdBy}
                        ownerId={dashboardInfo.ownerId}
                        createdOn={dashboardInfo.createdOn}
                        createdIn={dashboardInfo.createdIn}
                        title={dashboardInfo.title}
                        onActionCb={openDashboardPage(dashboardInfo)}
                        onClose={onDashboardInfoClose}
                        tags={dashboardInfo.tags}
                        widgetTitleList={dashboardInfo.widgetTitleList}
                        image={getImage(dashboardInfo.modalFormData)}
                    />
                ) : null}
            </RightSideBar>

            {shortcutAndHotkeysOptions.enableFavoriteShortcuts && (
                <ShortcutOpen
                    favorites={favoriteDashboards}
                    history={history}
                />
            )}

            <Onboarding />
        </div>
    );
};

DashboardLibrary.propTypes = {
    defaultImageBase64: PropTypes.string.isRequired,
    deleteDashboard: PropTypes.func.isRequired,
    history: PropTypes.objectOf(PropTypes.any),
    imageUploadOptions: PropTypes.arrayOf(PropTypes.any).isRequired,
    permissionSettingOptions: PropTypes.arrayOf(PropTypes.any).isRequired,
    updateModalFormData: PropTypes.func.isRequired,
    selectedOrganization: PropTypes.objectOf(PropTypes.any),
    shortcutAndHotkeysOptions: PropTypes.objectOf(PropTypes.any).isRequired,
    isFetchingDashboards: PropTypes.bool.isRequired,
    favoriteDashboards: PropTypes.arrayOf(PropTypes.any).isRequired,
    otherDashboards: PropTypes.arrayOf(PropTypes.any).isRequired,
    resourcesLoaded: PropTypes.objectOf(PropTypes.bool).isRequired,
    queryParams: PropTypes.objectOf(PropTypes.any).isRequired,
    selectedTagsOptions: PropTypes.arrayOf(PropTypes.any),
    changeDashboardLibraryFilters: PropTypes.func.isRequired,
    showDashboardsFilter: PropTypes.bool,
    isHelpIntroDisabled: PropTypes.bool,
    setDashboardExportMode: PropTypes.func.isRequired,
    dashboardLayout: PropTypes.string.isRequired
};

DashboardLibrary.defaultProps = {
    history: null,
    selectedOrganization: null,
    selectedTagsOptions: [],
    showDashboardsFilter: false,
    isHelpIntroDisabled: false
};

const mapStateToProps = ({ dashboardReducer, userReducer, tooltipsReducer }) => (
    {
        defaultImageBase64: dashboardReducer.defaultImageBase64,
        permissionSettingOptions: dashboardReducer.permissionSettingOptions,
        imageUploadOptions: dashboardReducer.imageUploadOptions,
        dashboards: dashboardReducer.dashboards,
        selectedOrganization: userReducer.selectedOrganization,
        shortcutAndHotkeysOptions: userReducer.shortcutAndHotkeysOptions,
        isFetchingDashboards: dashboardReducer.isFetchingDashboards,
        favoriteDashboards: dashboardReducer.favoriteDashboards,
        otherDashboards: dashboardReducer.otherDashboards,
        resourcesLoaded: dashboardReducer.resourcesLoaded,
        queryParams: userReducer.queryParams,
        selectedTagsOptions: userReducer.selectedTagsOptions,
        selectedPermissionOption: userReducer.selectedPermissionOption,
        selectedSharedByOption: userReducer.selectedSharedByOption,
        showDashboardsFilter: userReducer.showDashboardsFilter,
        isMobileDevice: userReducer.isMobileDevice,
        isHelpIntroDisabled: tooltipsReducer.isHelpIntroDisabled,
        isExportMode: dashboardReducer.isExportMode,
        dashboardsForExport: dashboardReducer.dashboardsForExport,
        dashboardFiltersLoaded: userReducer.dashboardFiltersLoaded,
        dashboardLayout: userReducer.dashboardLayout
    }
);

const mapDispatchToProps = dispatch => (
    {
        deleteDashboard: (dashboard, query) => deleteDashboard(dispatch, dashboard, query),
        changeDashboardLibraryFilters: (
            queryParams,
            tagOptions,
            permissionOptions,
            sharedOptions,
            showDashboardsFilter
        ) => changeDashboardLibraryFilters(
            dispatch,
            queryParams,
            tagOptions,
            permissionOptions,
            sharedOptions,
            showDashboardsFilter
        ),
        updateModalFormData: (data, prop) => updateModalFormData(dispatch, data, prop),
        setDashboardExportMode: (isExportMode) => setDashboardExportMode(dispatch, isExportMode),
        removeAllDashboardsForExport: () => removeAllDashboardsForExport(dispatch),
        importDashboards: (dashboards) => importDashboards(dispatch, dashboards),
        exportDashboards: (dashboards) => exportDashboards(dispatch, dashboards),
        getAllDashboards: query => getAllDashboards(dispatch, query),
        changeDashboardLibraryLayout: layout => changeDashboardLibraryLayout(dispatch, layout),
        resetDashboardLibraryFilters: () => resetDashboardLibraryFilters(dispatch),
        setDashboardLibraryFilters: filtersData => setDashboardLibraryFilters(dispatch, filtersData),
    }
);

export default connect(mapStateToProps, mapDispatchToProps)(DashboardLibrary);
